// File: src/pages/UserProfile.jsx
import React from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Card,
  CardBody,
  FormGroup,
  Badge,
  Alert,
} from "reactstrap";
import swal from "sweetalert";

import {
  Create_CompanyDetails,
  EditUserProfile,
  ViewCompanyDetails,
  _Get,
  getData,
  makePlanUgradeRequest,
  _GetList,
} from "../import/ApiEndPoint/ApiCalling";

import UserContext from "../import/context/Context";

import {
  GST_Verification_Api,
  View_User_By_Id,
  country_state_City_List,
} from "../import/ApiEndPoint/Api";

import axios from "axios";
import { baseURL } from "../import/ApiEndPoint/TestApi";

/* ================= Financial Year Helpers ================= */

const getFinancialYearLabel = (date = new Date()) => {
  const d = new Date(date);
  const month = d.getMonth(); // Jan = 0, Apr = 3
  const year = d.getFullYear();

  const startYear = month >= 3 ? year : year - 1;
  const endYearShort = String(startYear + 1).slice(-2);

  return `${startYear}-${endYearShort}`;
};

const getPreviousFinancialYearLabel = () => {
  const currentFY = getFinancialYearLabel();
  const startYear = Number(String(currentFY).split("-")[0]);
  return `${startYear - 1}-${String(startYear).slice(-2)}`;
};

const getNextFinancialYearLabel = (fy) => {
  const startYear = Number(String(fy || getFinancialYearLabel()).split("-")[0]);
  const nextStart = Number.isFinite(startYear)
    ? startYear + 1
    : new Date().getFullYear() + 1;

  return `${nextStart}-${String(nextStart + 1).slice(-2)}`;
};

const getFyStartYear = (fy) => {
  const n = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(n) ? n : 0;
};

const normalizeInvoiceSeries = (company = {}) => {
  const currentFY = getFinancialYearLabel();
  const previousFY = getPreviousFinancialYearLabel();

  let raw =
    company?.invoiceSeries ||
    company?.invoicePrefixSuffix ||
    company?.prefixSuffixByFY ||
    [];

  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }

  let list = Array.isArray(raw) ? raw : [];

  list = list
    .map((x) => ({
      financialYear: x?.financialYear || x?.fy || "",
      Prefix: x?.Prefix ?? x?.prefix ?? "",
      Suffix: x?.Suffix ?? x?.suffix ?? "",
    }))
    .filter((x) => x.financialYear);

  // Backward compatibility:
  // Existing Prefix/Suffix is treated as previous FY if no FY-wise list exists.
  if (!list.length && (company?.Prefix || company?.Suffix)) {
    list.push({
      financialYear: previousFY,
      Prefix: company?.Prefix || "",
      Suffix: company?.Suffix || "",
    });
  }

  // Always show current FY row.
  if (!list.some((x) => x.financialYear === currentFY)) {
    list.push({
      financialYear: currentFY,
      Prefix: "",
      Suffix: "",
    });
  }

  return list.sort(
    (a, b) => getFyStartYear(a.financialYear) - getFyStartYear(b.financialYear),
  );
};

/* ============== Full Page Premium CSS ============== */

const injectOnce = () => {
  const old = document.getElementById("user-profile-inline-css");
  if (old) old.remove();

  const s = document.createElement("style");
  s.id = "user-profile-inline-css";
  s.innerHTML = `
    html,
    body {
      overflow-x: hidden !important;
    }

    #user-profile {
      position: fixed !important;
      top: 56px !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      max-width: 100vw !important;
      height: calc(100vh - 56px) !important;
      min-height: calc(100vh - 56px) !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      margin: 0 !important;
      padding: 14px !important;
      background:
        radial-gradient(circle at top left, rgba(255, 153, 0, 0.13), transparent 34%),
        radial-gradient(circle at top right, rgba(255, 56, 92, 0.11), transparent 30%),
        linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
      box-sizing: border-box;
      z-index: 45 !important;
    }

    #user-profile * {
      box-sizing: border-box;
    }

    .up-page {
      width: 100%;
      max-width: 100%;
      min-height: 100%;
    }

    .up-main-card {
      width: 100% !important;
      max-width: 100% !important;
      min-height: calc(100vh - 84px);
      border: 0 !important;
      border-radius: 24px !important;
      background: transparent !important;
      box-shadow: none !important;
      overflow: visible !important;
    }

    .up-topbar {
      position: sticky;
      top: 0;
      z-index: 50;
      width: 100%;
      border-radius: 22px;
      background: rgba(255, 255, 255, 0.94);
      backdrop-filter: blur(14px);
      border: 1px solid rgba(226, 232, 240, 0.9);
      box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
      padding: 14px 16px;
      margin-bottom: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .up-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 240px;
    }

    .up-avatar {
      width: 48px;
      height: 48px;
      border-radius: 16px;
      display: grid;
      place-items: center;
      background: linear-gradient(135deg, #ff9900, #ff385c);
      color: #fff;
      font-weight: 900;
      font-size: 18px;
      box-shadow: 0 12px 22px rgba(255, 99, 71, 0.28);
      flex: 0 0 auto;
    }

    .up-title {
      margin: 0;
      font-size: 22px;
      font-weight: 900;
      color: #0f172a;
      line-height: 1.1;
      letter-spacing: -0.03em;
    }

    .up-subtitle {
      margin: 4px 0 0;
      color: #64748b;
      font-size: 13px;
      font-weight: 600;
    }

    .up-role-pill {
      display: inline-flex;
      align-items: center;
      background: #111827;
      color: #fff;
      border-radius: 999px;
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
    }

    .up-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .up-btn {
      border: 0 !important;
      border-radius: 999px !important;
      font-weight: 800 !important;
      padding: 9px 15px !important;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.10);
    }

    .up-btn-primary {
      background: linear-gradient(135deg, #ff9900, #f97316) !important;
      color: #fff !important;
    }

    .up-btn-dark {
      background: linear-gradient(135deg, #0f172a, #334155) !important;
      color: #fff !important;
    }

    .up-btn-green {
      background: linear-gradient(135deg, #16a34a, #22c55e) !important;
      color: #fff !important;
    }

    .up-btn-red {
      background: linear-gradient(135deg, #ef4444, #f97316) !important;
      color: #fff !important;
    }

    .up-hero {
      width: 100%;
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.95);
      background:
        linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.94)),
        radial-gradient(circle at top right, rgba(255, 153, 0, 0.35), transparent 35%);
      color: #fff;
      padding: 18px;
      margin-bottom: 14px;
      box-shadow: 0 18px 46px rgba(15, 23, 42, 0.18);
      overflow: hidden;
      position: relative;
    }

    .up-hero-grid {
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: minmax(260px, 1.4fr) repeat(3, minmax(140px, 0.45fr));
      gap: 14px;
      align-items: stretch;
    }

    .up-hero-title {
      font-size: 24px;
      font-weight: 900;
      line-height: 1.1;
      margin: 0 0 6px;
      letter-spacing: -0.03em;
    }

    .up-hero-text {
      color: #cbd5e1;
      margin: 0;
      font-size: 13px;
      font-weight: 600;
    }

    .up-stat {
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.08);
      padding: 12px;
    }

    .up-stat-label {
      color: #cbd5e1;
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 5px;
    }

    .up-stat-value {
      color: #fff;
      font-size: 15px;
      font-weight: 900;
      word-break: break-word;
    }

    .up-section {
      width: 100%;
      max-width: 100%;
      border: 1px solid rgba(226, 232, 240, 0.95);
      border-radius: 22px;
      margin: 14px 0;
      padding: 0;
      background: #fff;
      overflow: hidden;
      box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
    }

    .up-section-head {
      padding: 15px 16px;
      background: linear-gradient(180deg, #ffffff, #f8fafc);
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .up-h {
      font-size: 16px;
      font-weight: 900;
      margin: 0;
      color: #0f172a;
      text-transform: uppercase;
      letter-spacing: -0.01em;
    }

    .up-section-sub {
      color: #64748b;
      font-size: 12px;
      font-weight: 600;
      margin-top: 4px;
    }

    .up-section-body {
      padding: 14px;
    }

    .up-pill {
      display: inline-flex;
      align-items: center;
      background: #fff7ed;
      border: 1px solid #fed7aa;
      border-radius: 999px;
      padding: 5px 10px;
      font-size: 12px;
      color: #c2410c;
      font-weight: 900;
      margin-left: 8px;
    }

    #user-profile label {
      font-size: 12px;
      font-weight: 800;
      color: #334155;
      margin-bottom: 5px;
    }

    #user-profile .form-control,
    #user-profile .form-select {
      min-height: 40px;
      border-radius: 13px !important;
      border: 1px solid #dbe3ef !important;
      background: #ffffff;
      font-size: 13px;
      font-weight: 600;
      color: #0f172a;
      box-shadow: none !important;
    }

    #user-profile textarea.form-control {
      min-height: 40px;
    }

    #user-profile .form-control:focus,
    #user-profile .form-select:focus {
      border-color: #ff9900 !important;
      box-shadow: 0 0 0 4px rgba(255, 153, 0, 0.14) !important;
    }

    .up-file-preview {
      width: 92px;
      height: 70px;
      object-fit: contain;
      margin: 6px 0;
      display: block;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      background: #f8fafc;
      padding: 5px;
    }

    .up-bank-row {
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      margin: 10px 0;
      padding: 12px;
      width: 100%;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(178px, 1fr));
      gap: 10px;
      background: linear-gradient(180deg, #ffffff, #fbfdff);
    }

    .up-bank-col {
      min-width: 0 !important;
      width: 100%;
      padding: 0;
    }

    .up-invoice-row {
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      padding: 12px;
      margin-bottom: 10px;
      background: linear-gradient(180deg, #ffffff, #f8fafc);
      width: 100%;
      overflow: hidden;
    }

    .up-invoice-row.locked {
      background: #f8fafc;
      opacity: 0.95;
    }

    .up-lock-note {
      font-size: 12px;
      color: #dc2626;
      font-weight: 700;
      margin-top: 5px;
      white-space: normal;
      word-break: break-word;
    }

    .up-alert-wrap {
      white-space: normal !important;
      word-break: break-word;
      overflow-wrap: anywhere;
      line-height: 1.45;
      border-radius: 16px !important;
      font-weight: 700;
      border: 1px solid #fed7aa !important;
      background: #fff7ed !important;
      color: #9a3412 !important;
    }

    .up-input-readonly {
      background: #f1f5f9 !important;
      cursor: not-allowed;
    }

    .up-footer-actions {
      position: sticky;
      bottom: 0;
      z-index: 40;
      width: 100%;
      margin-top: 14px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(12px);
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      box-shadow: 0 -10px 30px rgba(15, 23, 42, 0.08);
      display: flex;
      justify-content: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .up-checkline {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 700;
      color: #475569;
      margin: 0 !important;
    }

    .up-checkline input {
      width: 16px;
      height: 16px;
    }

    .up-small-link {
      cursor: pointer;
      font-size: 12px;
      color: #2563eb;
      display: inline-block;
      margin-top: 5px;
      font-weight: 800;
    }

    .up-color-panel {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      border-radius: 999px;
      background: #f8fafc;
      border: 1px solid #e5e7eb;
      padding: 6px 8px;
    }

    .up-color-panel input[type="color"] {
      width: 32px;
      height: 32px;
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .up-plan-card {
      border-radius: 18px;
      border: 1px solid #e5e7eb;
      background: linear-gradient(180deg, #ffffff, #f8fafc);
      padding: 12px;
    }

    @media (max-width: 1200px) {
      .up-hero-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 768px) {
      #user-profile {
        top: 56px !important;
        height: calc(100vh - 56px) !important;
        padding: 8px !important;
      }

      .up-topbar {
        align-items: flex-start;
        border-radius: 18px;
      }

      .up-brand {
        width: 100%;
      }

      .up-actions {
        width: 100%;
        justify-content: flex-start;
      }

      .up-hero-grid {
        grid-template-columns: 1fr;
      }

      .up-bank-row {
        grid-template-columns: 1fr;
      }

      .up-pill {
        margin-left: 0;
        margin-top: 6px;
      }
    }
  `;

  document.head.appendChild(s);
};

class UserProfile extends React.Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      firstName: "",
      lastName: "",
      email: "",
      appPassword: "",
      password: "",
      cnfmPassword: "",
      mobileNumber: "",
      geotagging: "",
      pincode: "",
      selectedCountry: null,
      selectedState: null,
      selectedCity: null,

      LoginData: {},
      userInfo: {},
      databaseName: "",

      CompanyDetails: {},
      companyName: "",
      CompanyAddress: "",
      CompanyNumber: "",
      gstNumber: "",
      redate: "",
      Prefix: "",
      Suffix: "",
      invoiceSeries: [],
      qr_code: "",
      upi_id: "",
      Companylogo: null,
      Signature: null,
      termsAndCondition: "",

      openingBalance: "",
      openingType: "",

      bankDetails: [
        {
          bankName: "",
          accountNumber: "",
          openingBalance: "",
          openingType: "",
          branchName: "",
          bankIFSC: "",
          bankAddress: "",
          bankMicr: "",
          upiId: "",
          gpay_PhonepayNumber: "",
        },
      ],

      planList: [],
      selectedPlan: "",
      selectedPlanDetails: null,
      amountToPay: 0,
      planModal: false,
      showPayment: false,
      paymentScreenshot: null,

      countryStateCity: [],

      modal: false,
      Createmode: false,
      Loading: "Submit",
      Addcolor: false,
    };
  }

  componentDidMount = async () => {
    injectOnce();

    const pagePermission = JSON.parse(localStorage.getItem("userData") || "{}");

    this.setState({
      LoginData: pagePermission || {},
      databaseName: pagePermission?.database || "",
      name: pagePermission?.name || "",
      email: pagePermission?.email || "",
      cnfmPassword: pagePermission?.Userinfo?.password || "",
    });

    this.fetchPlanList();
    this.loadCountryStateCity();

    await this.fetchUser();

    await this.loadCompanyDetails(
      pagePermission?._id,
      pagePermission?.database,
    );

    this.scrollToInvoiceSection();
    window.addEventListener("hashchange", this.scrollToInvoiceSection);
  };

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.scrollToInvoiceSection);
  }

  logoutAfterSave = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("userData");
      localStorage.removeItem("allowedPermission");

      sessionStorage.removeItem("token");
      sessionStorage.removeItem("userData");
      sessionStorage.removeItem("allowedPermission");

      window.dispatchEvent(new Event("userData:updated"));
    } catch {}

    window.location.replace("/login");
  };

  scrollToInvoiceSection = () => {
    if (window.location.hash === "#invoice-prefix-suffix-section") {
      setTimeout(() => {
        const el = document.getElementById("invoice-prefix-suffix-section");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 400);
    }
  };

  isCurrentInvoiceFY = (fy) => fy === getFinancialYearLabel();

  isPastInvoiceFY = (fy) =>
    getFyStartYear(fy) < getFyStartYear(getFinancialYearLabel());

  isEditableInvoiceFY = (fy) => !this.isPastInvoiceFY(fy);

  addInvoiceSeriesRow = () => {
    const invoiceSeries = [...(this.state.invoiceSeries || [])];

    const sorted = invoiceSeries
      .slice()
      .sort(
        (a, b) =>
          getFyStartYear(a.financialYear) - getFyStartYear(b.financialYear),
      );

    const lastFY =
      sorted.length > 0
        ? sorted[sorted.length - 1]?.financialYear
        : getFinancialYearLabel();

    const nextFY = getNextFinancialYearLabel(lastFY);

    const exists = invoiceSeries.some((x) => x.financialYear === nextFY);

    if (exists) {
      swal("This financial year row already exists.");
      return;
    }

    this.setState({
      invoiceSeries: [
        ...invoiceSeries,
        {
          financialYear: nextFY,
          Prefix: "",
          Suffix: "",
        },
      ].sort(
        (a, b) =>
          getFyStartYear(a.financialYear) - getFyStartYear(b.financialYear),
      ),
    });
  };

  handleInvoiceSeriesChange = (index, field, value) => {
    const invoiceSeries = [...(this.state.invoiceSeries || [])];
    const row = invoiceSeries[index];

    if (!row) return;

    if (!this.isEditableInvoiceFY(row.financialYear)) {
      swal("Old financial year Prefix/Suffix cannot be edited.");
      return;
    }

    const finalValue =
      field === "Suffix" ? String(value || "").replace(/[^0-9]/g, "") : value;

    invoiceSeries[index] = {
      ...row,
      [field]: finalValue,
    };

    const currentFY = getFinancialYearLabel();
    const currentRow =
      invoiceSeries.find((x) => x.financialYear === currentFY) || {};

    this.setState({
      invoiceSeries,
      Prefix: currentRow?.Prefix || "",
      Suffix: currentRow?.Suffix || "",
    });
  };

  getCurrentInvoiceSeriesRow = () => {
    const currentFY = getFinancialYearLabel();

    return (this.state.invoiceSeries || []).find(
      (x) => x.financialYear === currentFY,
    );
  };

  loadCountryStateCity = () => {
    _GetList(country_state_City_List)
      .then((res) => this.setState({ countryStateCity: res || [] }))
      .catch((e) => console.error("Error fetching Pincode DB:", e));
  };

  getLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.setState({ geotagging: `${latitude}, ${longitude}` });
      },
      (error) => {
        console.error("Error getting location: ", error);
        alert("Unable to fetch location. Please allow location access.");
      },
    );
  };

  fetchUser = async () => {
    const storedUser = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!storedUser?._id) return;

    try {
      const res = await _Get(View_User_By_Id, storedUser._id);
      const fetchedUser = res?.User || {};

      // Keep login-critical values when the profile API omits them.
      const mergedUser = {
        ...storedUser,
        ...fetchedUser,
        _id: fetchedUser?._id || storedUser?._id || "",
        database: fetchedUser?.database || storedUser?.database || "",
        rolename: fetchedUser?.rolename || storedUser?.rolename || {},
      };

      this.setState({
        userInfo: mergedUser,
        LoginData: mergedUser,
        databaseName: mergedUser?.database || "",
      });

      localStorage.setItem("userData", JSON.stringify(mergedUser));
    } catch (err) {
      console.log("fetchUser error:", err);
    }
  };

  loadCompanyDetails = async (uid, db) => {
    if (!uid || !db) return;

    await ViewCompanyDetails(uid, db)
      .then((res) => {
        const value = res?.CompanyDetail || {};

        const invoiceSeries = normalizeInvoiceSeries(value);
        const currentFY = getFinancialYearLabel();

        const currentInvoiceRow =
          invoiceSeries.find((x) => x.financialYear === currentFY) || {};

        this.setState({
          CompanyDetails: value,
          gstNumber: value?.gstNo || "",
          Prefix: value?.Prefix || currentInvoiceRow?.Prefix || "",
          Suffix: value?.Suffix || currentInvoiceRow?.Suffix || "",
          invoiceSeries,
          email: value?.email || this.state.email,
          CompanyNumber: value?.mobileNo || "",
          redate: value?.redate || value?.reDate || "",
          companyName: value?.name || "",
          CompanyAddress: value?.address || "",
          openingBalance: value?.openingBalance || "",
          openingType: value?.openingType || "",
          bankDetails: value?.bankDetails?.length
            ? value.bankDetails
            : [
                {
                  bankName: "",
                  accountNumber: "",
                  openingBalance: "",
                  openingType: "",
                  branchName: "",
                  bankIFSC: "",
                  bankAddress: "",
                  bankMicr: "",
                  upiId: "",
                  gpay_PhonepayNumber: "",
                },
              ],
          qr_code: value?.qr_code || "",
          termsAndCondition: value?.termsAndCondition || "",
        });
      })
      .catch((err) => console.log(err));
  };

  fetchPlanList = async () => {
    try {
      const res = await getData();
      this.setState({ planList: res?.Subscription || [] });
    } catch (_) {}
  };

  toggleModal = () => this.setState((p) => ({ modal: !p.modal }));
  togglePlanModal = () => this.setState((p) => ({ planModal: !p.planModal }));

  handleBankChange = (index, e) => {
    const { name, value } = e.target;
    const bankDetails = [...this.state.bankDetails];

    bankDetails[index][name] = value;

    this.setState({ bankDetails });
  };

  addBankAccount = () =>
    this.setState((prev) => ({
      bankDetails: [
        ...prev.bankDetails,
        {
          bankName: "",
          accountNumber: "",
          openingBalance: "",
          openingType: "",
          branchName: "",
          bankIFSC: "",
          bankAddress: "",
          bankMicr: "",
          upiId: "",
          gpay_PhonepayNumber: "",
        },
      ],
    }));

  removeBankAccount = (index) => {
    const bankDetails = [...this.state.bankDetails];

    bankDetails.splice(index, 1);

    this.setState({ bankDetails });
  };

  changeHandler = (e) => {
    const { name, type, files, value } = e.target;

    if (type === "file") {
      this.setState({ [name]: files[0] });
    } else {
      this.setState({ [name]: value });
    }
  };

  submitHandler = async (e) => {
    e.preventDefault();

    this.setState({ Loading: "Loading..." });

    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const data = new FormData();

    data.append(
      "firstName",
      this.state.firstName === ""
        ? this.state.userInfo.firstName || ""
        : this.state.firstName,
    );

    data.append("email", this.state.email || "");
    data.append("appPassword", this.state.appPassword || "");

    data.append(
      "Country",
      this.state.selectedCountry
        ? this.state.selectedCountry
        : this.state.userInfo.Country || "",
    );

    data.append(
      "State",
      this.state.selectedState
        ? this.state.selectedState
        : this.state.userInfo.State || "",
    );

    data.append(
      "City",
      this.state.selectedCity
        ? this.state.selectedCity
        : this.state.userInfo.City || "",
    );

    data.append("gstNumber", this.state.gstNumber || "");

    data.append(
      "pincode",
      this.state.pincode === "" || this.state.pincode === undefined
        ? this.state.userInfo.pincode || ""
        : this.state.pincode,
    );

    data.append(
      "mobileNumber",
      this.state.mobileNumber === ""
        ? this.state.userInfo.mobileNumber || ""
        : this.state.mobileNumber,
    );

    data.append(
      "geotagging",
      this.state.geotagging === ""
        ? this.state.userInfo.geotagging || ""
        : this.state.geotagging,
    );

    data.append(
      "password",
      this.state.password === ""
        ? this.state.userInfo.password || ""
        : this.state.password,
    );

    data.append("cnfmPassword", this.state.cnfmPassword || "");

    if (this.state.userInfo?.rolename?.roleName === "MASTER") {
      data.append(
        "qr_code",
        this.state.qr_code || this.state.userInfo.qr_code || "",
      );

      data.append(
        "upi_id",
        this.state.upi_id || this.state.userInfo.upi_id || "",
      );
    }

    try {
      if (
        (this.state.password || this.state.userInfo.password) &&
        this.state.cnfmPassword
      ) {
        if (
          (this.state.password || this.state.userInfo.password) !==
          this.state.cnfmPassword
        ) {
          swal("Password Does Not Match");
          this.setState({ Loading: "Submit" });
          return;
        }
      }

      const resp = await EditUserProfile(userData._id, data);

      if (resp?.status) {
        this.setState({ Loading: "Submit" });

        await swal(
          "Success!",
          "Updated Successfully. Please login again.",
          "success",
        );

        this.logoutAfterSave();
        return;
      }

      this.setState({ Loading: "Submit" });
    } catch (error) {
      swal("Error!", "Something went wrong", "error");
      this.setState({ Loading: "Submit" });
      console.log(error?.response || error);
    }
  };

  HandleUploadLogo = async (e) => {
    e.preventDefault();

    const pageparmission = JSON.parse(localStorage.getItem("userData") || "{}");

    const companyCreatedBy =
      typeof this.state.CompanyDetails?.created_by === "object"
        ? this.state.CompanyDetails?.created_by?._id
        : this.state.CompanyDetails?.created_by;

    const createdBy =
      this.state.userInfo?._id ||
      this.state.LoginData?._id ||
      pageparmission?._id ||
      companyCreatedBy ||
      "";

    const isValidCreatedBy = /^[a-fA-F0-9]{24}$/.test(String(createdBy));

    if (!isValidCreatedBy) {
      console.error("Invalid created_by value:", {
        createdBy,
        userInfo: this.state.userInfo,
        LoginData: this.state.LoginData,
        localStorageUser: pageparmission,
        CompanyDetails: this.state.CompanyDetails,
      });

      swal(
        "User ID Missing",
        "Your login user ID is missing or invalid. Please log out and log in again.",
        "error",
      );
      return;
    }

    const currentFY = getFinancialYearLabel();

    let invoiceSeries = [...(this.state.invoiceSeries || [])];

    const currentPrefix = this.state.Prefix || "";
    const currentSuffix = String(this.state.Suffix || "").replace(
      /[^0-9]/g,
      "",
    );

    if (!currentPrefix || !currentSuffix) {
      swal(
        "Prefix/Suffix Required",
        "Please enter Prefix and Suffix for invoice information.",
        "warning",
      );
      return;
    }

    const currentIndex = invoiceSeries.findIndex(
      (x) => x.financialYear === currentFY,
    );

    if (currentIndex >= 0) {
      invoiceSeries[currentIndex] = {
        ...invoiceSeries[currentIndex],
        Prefix: currentPrefix,
        Suffix: currentSuffix,
      };
    } else {
      invoiceSeries.push({
        financialYear: currentFY,
        Prefix: currentPrefix,
        Suffix: currentSuffix,
      });
    }

    invoiceSeries = invoiceSeries.sort(
      (a, b) =>
        getFyStartYear(a.financialYear) - getFyStartYear(b.financialYear),
    );

    const formData = new FormData();

    formData.append("created_by", String(createdBy));
    formData.append("database", pageparmission.database || "");
    formData.append("email", this.state.email || "");
    formData.append("name", this.state.companyName || "");
    formData.append("mobileNo", this.state.CompanyNumber || "");

    if (this.state.Companylogo) {
      formData.append("file", this.state.Companylogo);
    }

    if (this.state.Signature) {
      formData.append("signature", this.state.Signature);
    }

    if (this.state.qr_code) {
      formData.append("qr_code", this.state.qr_code);
    }

    formData.append("gstNo", this.state.gstNumber || "");
    formData.append("Prefix", currentPrefix || "");
    formData.append("Suffix", currentSuffix || "");
    formData.append("invoiceSeries", JSON.stringify(invoiceSeries || []));
    formData.append("activeFinancialYear", currentFY);
    formData.append("activeInvoicePrefix", currentPrefix || "");
    formData.append("activeInvoiceSuffix", currentSuffix || "");
    formData.append("address", this.state.CompanyAddress || "");
    formData.append("openingBalance", this.state.openingBalance || "");
    formData.append("openingType", this.state.openingType || "");
    formData.append("reDate", this.state.redate || "");
    formData.append(
      "bankDetails",
      JSON.stringify(this.state.bankDetails || []),
    );

    try {
      await Create_CompanyDetails(formData);

      await swal(
        "Success!",
        "Company and Bank Details are Added/Updated Successfully. Please login again.",
        "success",
      );

      this.logoutAfterSave();
    } catch (err) {
      console.log(err);
      swal("Error!", "Could not save company/bank details", "error");
    }
  };

  handlePlanChange = (event) => {
    const selectedPlanId = event.target.value;

    const selectedPlan = this.state.planList.find(
      (p) => p._id === selectedPlanId,
    );

    if (!selectedPlan) return;

    const currentPlan = this.state.userInfo?.subscriptionPlan;
    let amountToPay = selectedPlan.subscriptionCost;

    if (currentPlan && currentPlan.planName !== selectedPlan.planName) {
      const planStartDate = new Date(this.state.userInfo.planStart);
      const today = new Date();

      const daysUsed =
        Math.floor((today - planStartDate) / (1000 * 60 * 60 * 24)) + 1;

      if (currentPlan.days && selectedPlan.days) {
        const currentPlanDays = currentPlan.days;
        const dailyRate = currentPlan.subscriptionCost / currentPlanDays;
        const remainingBalance = (currentPlanDays - daysUsed) * dailyRate;

        amountToPay = selectedPlan.subscriptionCost - remainingBalance;
      }
    }

    this.setState({
      selectedPlan: selectedPlanId,
      selectedPlanDetails: selectedPlan,
      amountToPay: Math.ceil(Math.max(amountToPay, 0)),
    });
  };

  showPaymentOptions = () => this.setState({ showPayment: true });

  handleFileUpload = (e) =>
    this.setState({ paymentScreenshot: e.target.files[0] });

  requestPlanUpdate = async () => {
    if (!this.state.paymentScreenshot) {
      alert("Please upload a payment screenshot.");
      return;
    }

    const pageparmission = JSON.parse(localStorage.getItem("userData") || "{}");

    const formData = new FormData();

    formData.append("plan", this.state.selectedPlan);
    formData.append("file", this.state.paymentScreenshot);
    formData.append("superAdmin", pageparmission?._id || "");
    formData.append("amount", this.state.amountToPay || 0);
    formData.append("message", "Request for Plan Upgrade");
    formData.append(
      "PrevPlan",
      this.state.userInfo?.subscriptionPlan?._id || "",
    );

    try {
      const response = await makePlanUgradeRequest(formData);

      if (response?.status) {
        swal("Success!", "Plan update request sent successfully", "success");

        const newMessage = {
          sender: "SuperAdmin",
          text: "A new request for plan update. Please review the plan request.",
          date: Date.now(),
        };

        await axios.post(`${baseURL}/chat/save-chat`, {
          superadmin: pageparmission?._id || "",
          messages: [newMessage],
        });
      }

      this.togglePlanModal();
    } catch (error) {
      swal("Error!", "Error in updating plan", "error");
    }
  };

  updatePlan = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      const data = new FormData();

      data.append("subscriptionPlan", this.state.selectedPlan);

      const response = await EditUserProfile(userData._id, data);

      if (response?.status) {
        swal("Success!", "Plan Updated Successfully", "success");
        this.setState({ Loading: "Submit" });
        await this.fetchUser();
      }

      this.togglePlanModal();
    } catch (e) {}
  };

  renderPlanDetails() {
    if (this.state.userInfo?.rolename?.roleName !== "SuperAdmin") return null;

    const sp = this.state.userInfo?.subscriptionPlan || {};

    return (
      <CardBody className="up-section">
        <div className="up-section-head">
          <div>
            <div className="up-h">Plan Details</div>
            <div className="up-section-sub">
              Subscription, validity and upgrade details.
            </div>
          </div>

          <Button className="up-btn up-btn-dark" onClick={this.togglePlanModal}>
            Upgrade Plan
          </Button>
        </div>

        <div className="up-section-body">
          <Row className="m-0">
            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>Plan Name</Label>
              <Input type="text" value={sp?.planName || ""} readOnly />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>Plan Type</Label>
              <Input
                type="text"
                value={sp?.subscriptionType === 1 ? "Yearly" : "One Time"}
                readOnly
              />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>User Allowed</Label>
              <Input type="text" value={sp?.noOfUser || ""} readOnly />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>Plan Amount</Label>
              <Input
                type="text"
                value={this.state.userInfo?.billAmount || ""}
                readOnly
              />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>Plan Status</Label>
              <Input
                type="text"
                value={this.state.userInfo?.planStatus || ""}
                readOnly
              />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>Start Date</Label>
              <Input
                type="text"
                value={
                  (this.state.userInfo?.planStart || "")
                    .toString()
                    .split("GMT")[0] || ""
                }
                readOnly
              />
            </Col>

            <Col lg="2" md="3" sm="6" className="p-1">
              <Label>End Date</Label>
              <Input
                type="text"
                value={
                  (this.state.userInfo?.planEnd || "")
                    .toString()
                    .split("GMT")[0] || ""
                }
                readOnly
              />
            </Col>
          </Row>

          <p className="up-small-link">
            Note: Please read terms and conditions for updating plan details.
          </p>
        </div>
      </CardBody>
    );
  }

  render() {
    const role = this.state.LoginData?.rolename?.roleName;

    const displayName =
      this.state.userInfo?.firstName ||
      this.state.LoginData?.firstName ||
      this.state.companyName ||
      "User";

    return (
      <React.Fragment>
        <div id="user-profile">
          <div className="up-page">
            <Card className="up-main-card">
              <div className="up-topbar">
                <div className="up-brand">
                  <div className="up-avatar">
                    {String(displayName || "U")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>

                  <div>
                    <h1 className="up-title">Profile & Company Settings</h1>
                    <p className="up-subtitle">
                      Manage account, company, bank and invoice settings.
                    </p>
                  </div>
                </div>

                <div className="up-actions">
                  {role ? <span className="up-role-pill">{role}</span> : null}

                  <Button
                    className="up-btn up-btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      this.setState({ Addcolor: true });
                    }}
                  >
                    Theme Color
                  </Button>

                  {this.state.Addcolor && (
                    <div className="up-color-panel">
                      <input
                        onChange={(e) => {
                          localStorage.setItem(
                            "UserDefinedcoler",
                            e.target.value,
                          );
                          this.context?.SetmyCustomColor?.(e.target.value);
                        }}
                        type="color"
                      />

                      <Badge
                        color="dark"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.preventDefault();
                          this.setState({ Addcolor: false });
                        }}
                      >
                        Done
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              <div className="up-hero">
                <div className="up-hero-grid">
                  <div>
                    <h2 className="up-hero-title">Welcome, {displayName}</h2>
                    <p className="up-hero-text">
                      Your settings are shown in full-screen view. Scroll down
                      to update company, bank and financial year invoice
                      details.
                    </p>
                  </div>

                  <div className="up-stat">
                    <div className="up-stat-label">Database</div>
                    <div className="up-stat-value">
                      {this.state.databaseName || "-"}
                    </div>
                  </div>

                  <div className="up-stat">
                    <div className="up-stat-label">Current FY</div>
                    <div className="up-stat-value">
                      {getFinancialYearLabel()}
                    </div>
                  </div>

                  <div className="up-stat">
                    <div className="up-stat-label">Company</div>
                    <div className="up-stat-value">
                      {this.state.companyName || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* ===================== PERSONAL DETAILS ===================== */}
              <Form onSubmit={this.submitHandler}>
                <CardBody className="up-section">
                  <div className="up-section-head">
                    <div>
                      <div className="up-h">Personal Details</div>
                      <div className="up-section-sub">
                        Update login, contact and location information.
                      </div>
                    </div>
                  </div>

                  <div className="up-section-body">
                    <Row className="m-0">
                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>First Name</Label>
                        <Input
                          type="text"
                          name="firstName"
                          placeholder="First Name"
                          value={
                            this.state.firstName ||
                            this.state.userInfo?.firstName ||
                            ""
                          }
                          onChange={this.changeHandler}
                        />
                      </Col>

                      <Col sm="6" lg="3" md="3" className="p-1">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          name="email"
                          placeholder="Email"
                          value={
                            this.state.email || this.state.userInfo?.email || ""
                          }
                          onChange={this.changeHandler}
                        />
                      </Col>

                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>App Password</Label>
                        <Input
                          type="password"
                          name="appPassword"
                          placeholder="App Password"
                          value={this.state.appPassword || ""}
                          onChange={this.changeHandler}
                        />
                      </Col>

                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>Pincode</Label>
                        <Input
                          type="number"
                          name="pincode"
                          placeholder="Pincode"
                          value={
                            this.state.pincode ||
                            this.state.userInfo?.pincode ||
                            ""
                          }
                          onChange={(e) => {
                            const value = e.target.value;

                            const rec = this.state.countryStateCity.find(
                              (d) => d.Pincode === value,
                            );

                            if (rec) {
                              this.setState({
                                pincode: value,
                                selectedCity: rec?.District || "",
                                selectedState: rec?.StateName || "",
                                selectedCountry: "India",
                              });
                            } else {
                              this.setState({
                                pincode: value,
                                selectedCity: null,
                                selectedState: null,
                                selectedCountry: null,
                              });
                            }
                          }}
                        />
                      </Col>

                      <Col lg="2" md="3" sm="6" className="p-1">
                        <FormGroup>
                          <Label>Country</Label>
                          <Input
                            type="text"
                            placeholder="Country"
                            value={
                              this.state.selectedCountry ??
                              this.state.userInfo?.Country ??
                              ""
                            }
                            readOnly
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="2" md="3" sm="6" className="p-1">
                        <FormGroup>
                          <Label>State</Label>
                          <Input
                            type="text"
                            placeholder="State"
                            value={
                              this.state.selectedState ||
                              this.state.userInfo?.State ||
                              ""
                            }
                            readOnly
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="2" md="3" sm="6" className="p-1">
                        <FormGroup>
                          <Label>City</Label>
                          <Input
                            type="text"
                            placeholder="City"
                            value={
                              this.state.selectedCity ||
                              this.state.userInfo?.City ||
                              ""
                            }
                            readOnly
                          />
                        </FormGroup>
                      </Col>

                      <Col sm="6" lg="3" md="3" className="p-1">
                        <Label>Geo Location</Label>
                        <Input
                          type="text"
                          name="geotagging"
                          placeholder="geotagging"
                          value={
                            this.state.geotagging ||
                            this.state.userInfo?.geotagging ||
                            ""
                          }
                          onChange={this.changeHandler}
                        />

                        <span
                          className="up-small-link"
                          onClick={this.getLocation}
                        >
                          Update Location
                        </span>
                      </Col>

                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>Mobile Number</Label>
                        <Input
                          type="number"
                          name="mobileNumber"
                          placeholder="Mobile Number"
                          value={
                            this.state.mobileNumber ||
                            this.state.userInfo?.mobileNumber ||
                            ""
                          }
                          onChange={this.changeHandler}
                        />
                      </Col>

                      {this.state.userInfo?.rolename?.roleName === "MASTER" && (
                        <>
                          <Col sm="6" lg="2" md="3" className="p-1">
                            <Label>Upload QR Image</Label>

                            {this.state.userInfo?.qr_code ? (
                              <img
                                className="up-file-preview"
                                src={`${baseURL}/Images/${this.state.userInfo.qr_code}`}
                                alt="QR"
                              />
                            ) : null}

                            <Input
                              type="file"
                              name="qr_code"
                              onChange={this.changeHandler}
                            />
                          </Col>

                          <Col sm="6" lg="2" md="3" className="p-1">
                            <Label>Update UPI ID</Label>
                            <Input
                              type="text"
                              name="upi_id"
                              value={
                                this.state.upi_id ||
                                this.state.userInfo?.upi_id ||
                                ""
                              }
                              onChange={this.changeHandler}
                            />
                          </Col>
                        </>
                      )}

                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>Password</Label>
                        <Input
                          type="password"
                          name="password"
                          placeholder="Password"
                          value={this.state.password || ""}
                          onChange={this.changeHandler}
                        />
                      </Col>

                      <Col sm="6" lg="2" md="3" className="p-1">
                        <Label>Confirm Password</Label>
                        <Input
                          type="password"
                          name="cnfmPassword"
                          placeholder="Confirm Password"
                          value={this.state.cnfmPassword || ""}
                          onChange={this.changeHandler}
                        />
                      </Col>

                      <Col
                        xs="12"
                        className="p-1 d-flex justify-content-between align-items-center flex-wrap gap-2"
                      >
                        <label className="up-checkline">
                          <Input type="checkbox" defaultChecked id="terms1" />I
                          accept the terms &amp; conditions.
                        </label>

                        <Button className="up-btn up-btn-dark" type="submit">
                          {this.state.Loading}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Form>

              {/* ===================== COMPANY INFORMATION ===================== */}
              {this.state.LoginData?.rolename?.roleName === "SuperAdmin" && (
                <CardBody className="up-section">
                  <Form onSubmit={this.HandleUploadLogo}>
                    <div className="up-section-head">
                      <div>
                        <div className="up-h">Company Information</div>
                        <div className="up-section-sub">
                          Manage business identity, GST, logo and contact
                          details.
                        </div>
                      </div>
                    </div>

                    <div className="up-section-body">
                      <Row className="m-0">
                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Signature</Label>

                          {this.state.CompanyDetails?.signature ? (
                            <img
                              className="up-file-preview"
                              src={`${baseURL}/Images/${this.state.CompanyDetails.signature}`}
                              alt="Signature"
                            />
                          ) : null}

                          <Input
                            type="file"
                            name="Signature"
                            onChange={(e) =>
                              this.setState({ Signature: e.target.files[0] })
                            }
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Company Logo</Label>

                          {this.state.CompanyDetails?.logo ? (
                            <img
                              className="up-file-preview"
                              src={`${baseURL}/Images/${this.state.CompanyDetails.logo}`}
                              alt="Company Logo"
                            />
                          ) : null}

                          <Input
                            type="file"
                            name="Companylogo"
                            onChange={(e) =>
                              this.setState({
                                Companylogo: e.target.files[0],
                              })
                            }
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Upload QR Image</Label>

                          {this.state.qr_code ? (
                            <img
                              className="up-file-preview"
                              src={
                                typeof this.state.qr_code === "string"
                                  ? `${baseURL}/Images/${this.state.qr_code}`
                                  : ""
                              }
                              alt="qr"
                            />
                          ) : null}

                          <Input
                            type="file"
                            name="qr_code"
                            onChange={(e) =>
                              this.setState({ qr_code: e.target.files[0] })
                            }
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>GST Number *</Label>

                          <Input
                            type="text"
                            required
                            minLength={15}
                            maxLength={15}
                            name="gstNumber"
                            placeholder="Enter Correct GST Number"
                            value={
                              this.state.gstNumber ||
                              this.state.userInfo?.Gst_No ||
                              ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;

                              this.setState({ gstNumber: value });

                              if (value && value.length === 15) {
                                (async () => {
                                  try {
                                    const res = await _Get(
                                      GST_Verification_Api,
                                      value,
                                    );

                                    if (res?.flag) {
                                      this.setState({
                                        CompanyAddress:
                                          res?.data?.pradr?.adr || "",
                                        companyName: res?.data?.tradeNam || "",
                                        redate: res?.data?.rgdt || "",
                                      });
                                    } else {
                                      swal(
                                        "Error",
                                        res?.message || "Invalid GST",
                                      );
                                    }
                                  } catch (err) {
                                    swal("Error", "Something went wrong");
                                  }
                                })();
                              }
                            }}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Registration Date</Label>

                          <Input
                            type="date"
                            name="redate"
                            value={this.state.redate || ""}
                            onChange={this.changeHandler}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Company Name</Label>

                          <Input
                            required
                            type="text"
                            name="companyName"
                            placeholder="Company Name"
                            value={
                              this.state.companyName ||
                              this.state.userInfo?.companyName ||
                              ""
                            }
                            onChange={this.changeHandler}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Company Address</Label>

                          <Input
                            required
                            type="textarea"
                            name="CompanyAddress"
                            placeholder="Company Address"
                            value={
                              this.state.CompanyAddress ||
                              this.state.userInfo?.address2 ||
                              ""
                            }
                            onChange={this.changeHandler}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Company Number</Label>

                          <Input
                            required
                            type="text"
                            name="CompanyNumber"
                            placeholder="Company Number"
                            value={this.state.CompanyNumber || ""}
                            onChange={this.changeHandler}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Company Email</Label>

                          <Input
                            required
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={this.state.email || ""}
                            onChange={this.changeHandler}
                          />
                        </Col>
                      </Row>
                    </div>

                    {/* ===================== CASH INFORMATION ===================== */}
                    <div className="up-section-head">
                      <div>
                        <div className="up-h">Cash Information</div>
                        <div className="up-section-sub">
                          Opening cash balance for accounting reports.
                        </div>
                      </div>
                    </div>

                    <div className="up-section-body">
                      <Row className="m-0">
                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Opening Balance Cash</Label>

                          <Input
                            type="number"
                            name="openingBalance"
                            placeholder="Opening Balance"
                            value={this.state.openingBalance || ""}
                            onChange={this.changeHandler}
                          />
                        </Col>

                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Opening Type</Label>

                          <Input
                            type="select"
                            name="openingType"
                            value={this.state.openingType || ""}
                            onChange={this.changeHandler}
                          >
                            <option value="">Select</option>
                            <option value="debit">Debit</option>
                            <option value="credit">Credit</option>
                          </Input>
                        </Col>
                      </Row>
                    </div>

                    {/* ===================== BANK INFORMATION ===================== */}
                    <div className="up-section-head">
                      <div>
                        <div className="up-h">Bank Information</div>
                        <div className="up-section-sub">
                          Add bank accounts used in receipts, payments and
                          invoices.
                        </div>
                      </div>

                      <Button
                        className="up-btn up-btn-primary"
                        type="button"
                        onClick={this.addBankAccount}
                      >
                        + Add Bank
                      </Button>
                    </div>

                    <div className="up-section-body">
                      {this.state.bankDetails.map((account, index) => (
                        <div className="up-bank-row" key={index}>
                          <div className="up-bank-col">
                            <Label>Bank Name</Label>

                            <Input
                              required
                              type="text"
                              name="bankName"
                              placeholder="Bank Name"
                              value={account.bankName}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>Account No.</Label>

                            <Input
                              required
                              type="text"
                              name="accountNumber"
                              placeholder="Account Number"
                              value={account.accountNumber}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>Opening Balance</Label>

                            <Input
                              required
                              type="number"
                              name="openingBalance"
                              placeholder="Opening Balance"
                              value={account.openingBalance}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>Opening Type</Label>

                            <Input
                              required
                              type="select"
                              name="openingType"
                              value={account.openingType}
                              onChange={(e) => this.handleBankChange(index, e)}
                            >
                              <option value="">Select</option>
                              <option value="debit">Debit</option>
                              <option value="credit">Credit</option>
                            </Input>
                          </div>

                          <div className="up-bank-col">
                            <Label>Branch Name</Label>

                            <Input
                              type="text"
                              name="branchName"
                              placeholder="Branch Name"
                              value={account.branchName}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>IFSC</Label>

                            <Input
                              type="text"
                              name="bankIFSC"
                              placeholder="IFSC Code"
                              value={account.bankIFSC}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>Address</Label>

                            <Input
                              type="text"
                              name="bankAddress"
                              placeholder="Address"
                              value={account.bankAddress}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>MICR</Label>

                            <Input
                              type="text"
                              name="bankMicr"
                              placeholder="MICR Code"
                              value={account.bankMicr}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>UPI ID</Label>

                            <Input
                              type="text"
                              name="upiId"
                              placeholder="UPI ID"
                              value={account.upiId}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col">
                            <Label>GPay/PhonePe</Label>

                            <Input
                              type="text"
                              name="gpay_PhonepayNumber"
                              placeholder="GPay/PhonePe"
                              value={account.gpay_PhonepayNumber}
                              onChange={(e) => this.handleBankChange(index, e)}
                            />
                          </div>

                          <div className="up-bank-col d-flex align-items-end">
                            {this.state.bankDetails.length > 1 && (
                              <Button
                                className="up-btn up-btn-red"
                                size="sm"
                                type="button"
                                onClick={() => this.removeBankAccount(index)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* ===================== INVOICE INFORMATION ===================== */}
                    <div
                      className="up-section-head"
                      id="invoice-prefix-suffix-section"
                    >
                      <div>
                        <div className="up-h">Invoice Information</div>

                        <div className="up-section-sub">
                          Save invoice Prefix and Suffix.
                        </div>
                      </div>
                    </div>

                    <div className="up-section-body">
                      <Alert
                        color="warning"
                        className="up-alert-wrap"
                        style={{ marginBottom: 12 }}
                      >
                        Enter the invoice Prefix and Suffix. These values will
                        be saved in company invoice information and used while
                        generating invoices.
                      </Alert>

                      <div className="up-invoice-row">
                        <Row className="m-0">
                          <Col sm="12" lg="3" md="4" className="p-1">
                            <Label>Bill Prefix</Label>

                            <Input
                              required
                              type="text"
                              name="Prefix"
                              placeholder="Prefix Enter here"
                              value={this.state.Prefix || ""}
                              onChange={(e) =>
                                this.setState({ Prefix: e.target.value })
                              }
                            />
                          </Col>

                          <Col sm="12" lg="3" md="4" className="p-1">
                            <Label>Bill Suffix</Label>

                            <Input
                              required
                              type="text"
                              name="Suffix"
                              placeholder="Suffix Enter here"
                              value={this.state.Suffix || ""}
                              onChange={(e) =>
                                this.setState({
                                  Suffix: String(e.target.value || "").replace(
                                    /[^0-9]/g,
                                    "",
                                  ),
                                })
                              }
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>

                    {/* ===================== DATABASE INFORMATION ===================== */}
                    <div className="up-section-head">
                      <div>
                        <div className="up-h">Database Information</div>
                        <div className="up-section-sub">
                          Read-only connected database name.
                        </div>
                      </div>
                    </div>

                    <div className="up-section-body">
                      <Row className="m-0">
                        <Col sm="12" lg="3" md="4" className="p-1">
                          <Label>Database Name</Label>

                          <Input
                            type="text"
                            value={this.state.databaseName || ""}
                            readOnly
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="up-footer-actions">
                      <label className="up-checkline">
                        <Input type="checkbox" defaultChecked id="terms2" />I
                        accept the terms &amp; conditions.
                      </label>

                      <Button className="up-btn up-btn-primary" type="submit">
                        Save Company & Bank
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              )}

              {this.renderPlanDetails()}
            </Card>
          </div>
        </div>

        {/* ===================== PLAN MODAL ===================== */}
        <Modal isOpen={this.state.planModal} toggle={this.togglePlanModal}>
          <ModalHeader toggle={this.togglePlanModal}>
            Choose a Different Plan
          </ModalHeader>

          <ModalBody>
            <Label>Select Plan</Label>

            <Input
              type="select"
              onChange={this.handlePlanChange}
              value={this.state.selectedPlan}
              className="mb-2"
            >
              <option value="">Select a plan</option>

              {this.state.planList.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.planName}
                </option>
              ))}
            </Input>

            {this.state.selectedPlanDetails && (
              <div className="up-plan-card">
                <h6 className="mb-2">Plan Details</h6>

                <p className="mb-1">
                  <strong>Name:</strong>{" "}
                  {this.state.selectedPlanDetails.planName}
                </p>

                <p className="mb-1">
                  <strong>Users:</strong>{" "}
                  {this.state.selectedPlanDetails.noOfUser}
                </p>

                <p className="mb-1">
                  <strong>Cost:</strong> ₹
                  {this.state.selectedPlanDetails.subscriptionCost}
                </p>

                {this.state.amountToPay > 0 && (
                  <p className="mb-2">
                    <strong>Amount to Pay:</strong> ₹{this.state.amountToPay}
                  </p>
                )}

                <Button
                  className="up-btn up-btn-primary"
                  block
                  onClick={this.showPaymentOptions}
                >
                  Proceed
                </Button>
              </div>
            )}

            {this.state.showPayment && (
              <div className="mt-3 p-3 border rounded bg-light">
                <h6 className="mb-2">Make Payment</h6>

                <div className="d-flex align-items-center justify-content-between border p-2 rounded bg-white">
                  {this.state.userInfo?.created_by?.qr_code ? (
                    <img
                      src={`${baseURL}/Images/${this.state.userInfo.created_by.qr_code}`}
                      alt="QR Code"
                      className="up-file-preview"
                    />
                  ) : (
                    <p className="text-muted small m-0">No QR Image</p>
                  )}

                  <p className="mb-0 text-center">
                    <strong>UPI ID:</strong>
                    <br />
                    <span>
                      {this.state?.userInfo?.created_by?.upi_id || "No UPI ID"}
                    </span>
                  </p>
                </div>

                <Label className="mt-2">Upload Payment Screenshot</Label>

                <Input
                  type="file"
                  name="paymentScreenshot"
                  onChange={this.handleFileUpload}
                />

                <Button
                  className="up-btn up-btn-green mt-2"
                  block
                  onClick={this.requestPlanUpdate}
                >
                  Request for Update
                </Button>

                <p className="text-muted small text-center mt-1">
                  Usually, it requires 1–2 days for the plan to get updated.
                </p>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={this.togglePlanModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

export default UserProfile;
